/**
 * My Portfolio Hook
 *
 * 내 포트폴리오 조회 및 존재 여부 확인 훅
 * - 로그인 시 자동으로 포트폴리오 조회
 * - hasPortfolio 플래그로 리더 탭 표시 여부 결정
 */

import { useState, useEffect } from 'react';
import { useToken } from '@app/hooks';
import { getMyPortfolio } from '../../api/copyTrading';
import type { CopyPortfolio } from '../../../types/copyTrading';

/**
 * 내 포트폴리오 조회 및 존재 여부 확인
 *
 * @returns { portfolio, hasPortfolio, isLoading, refetch }
 */
export function useMyPortfolio() {
  const { token } = useToken();
  const [portfolio, setPortfolio] = useState<CopyPortfolio | null>(null);
  const [hasPortfolio, setHasPortfolio] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPortfolio = async () => {
    if (!token) {
      setPortfolio(null);
      setHasPortfolio(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await getMyPortfolio();

      if (response.success && response.data) {
        setPortfolio(response.data);
        setHasPortfolio(true);
      } else {
        setPortfolio(null);
        setHasPortfolio(false);
      }
    } catch (error) {
      console.error('[useMyPortfolio] Failed to fetch portfolio:', error);
      setPortfolio(null);
      setHasPortfolio(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPortfolio();
  }, [token]);

  return {
    /** 내 포트폴리오 정보 (없으면 null) */
    portfolio,
    /** 포트폴리오 보유 여부 (리더 탭 표시 조건) */
    hasPortfolio,
    /** 로딩 상태 */
    isLoading,
    /** 수동으로 다시 조회 */
    refetch: fetchPortfolio,
  };
}
